import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import config from "lib/fr-config";
import TextButtonView from "presentational/common/TextButtonView";
import IconButtonView from "presentational/common/IconButtonView";
// import GenderLocationInfoText from "presentational/common/multinational/GenderLocationInfoText";
import FolderListModalView from "presentational/common/modals/FolderListModalView";
import DateText from "presentational/common/multinational/DateText";
import NameTagTooltipView from "presentational/outfitDetail/NameTagTooltipView";
import { withStyles } from "@material-ui/core/styles";
import { getDeviceType } from "lib/deviceInfo";
import { getFullUrl, getEssentialUrl } from "lib/urlQueryParser";
import Typography from "@material-ui/core/Typography";
import PropTypes from "prop-types";

/**
 * NAMETAG_FIXED_HEIGHT_FOR_DESKTOP 값은
 * 내 아웃핏디테일의 네임태그와 타인 네임태그의 높이가 다른데(표현되는 텍스트 라인수가 다르다)
 * 이로인해 데스크탑 버전에서는 유동적으로 네임태그의 높이에 따라 이미지의 높이가 자동 지정되고 있다.
 * 그런데 내 아웃핏디테일 접근시 2줄이였던 네임태그가 갑자기 3줄이 되면서 이미지 작아지는 현상 발생.
 * 그래서 데스크탑에 한해 가장 긴 3줄용 네임태그의 높이에 자동으로 고정되도록 하기 위한 고정값.
 * 이 값은 현재 폰트와 간격등을 고려한 실 높이(72.19px, 패딩포함)보다 여유있게 작성함
 * !!나중에 값 변경시 이 변수값도 변경되어야 한다.
 */
const NAMETAG_FIXED_HEIGHT_FOR_DESKTOP = 74;

const USERNAME_FONT_SIZE = 14;
const INTO_FONT_SIZE = 12;

const fontCss = config.css.fontThickness;
const MORE_BTN_TYPE = config.iconDesign.moreButton;

const CustomTypography = withStyles({
  root: { marginTop: 0, marginBottom: 0, lineHeight: 1.5 },
  subtitle1: { fontSize: INTO_FONT_SIZE }
})(Typography);

const css = {
  rootWrapperForDesktop: {
    height: NAMETAG_FIXED_HEIGHT_FOR_DESKTOP
  },
  root: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    position: "relative"
  },
  nametagArea: {
    paddingTop: 10,
    flexGrow: 1
  },
  textWrapper: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    alignItems: "center"
  },
  textWrapperNowLoading: { opacity: 0 },
  // secondLineTextWrapper: { marginTop: -4 },
  thirtLineTextRemove: { display: "none" },
  thirtLineTextInvisible: { opacity: 0 },
  btnArea: {
    position: "absolute",
    top: 10,
    right: 0,
    bottom: 0,

    display: "flex",
    alignItems: "center"
  }
};

NameTagView.propTypes = {
  info: PropTypes.shape({
    ownerName: PropTypes.string,
    profileImage: PropTypes.string,
    /*gender: PropTypes.string,
    userLocation: PropTypes.string,*/
    outfitImage: PropTypes.string
  }).isRequired,
  typeOfUser: PropTypes.oneOf(["pending", "owner", "others", "guest"])
    .isRequired,
  calendarData: PropTypes.object,
  bookmarkData: PropTypes.object,
  bookmarkFolderList: PropTypes.array.isRequired,
  pickData: PropTypes.object,
  pickFolderList: PropTypes.array.isRequired,
  onBookmarkChange: PropTypes.func.isRequired,
  onPickChange: PropTypes.func.isRequired,
  onBookmarkFolderCreateBtnSubmit: PropTypes.func.isRequired,
  onPickFolderCreateBtnSubmit: PropTypes.func.isRequired,
  onBookmarkFolderListRequest: PropTypes.func.isRequired,
  onPickFolderListRequest: PropTypes.func.isRequired,
  onMoreBtnClick: PropTypes.func.isRequired
};

function NameTagView(props) {
  const {
    info,
    typeOfUser,
    calendarData,
    bookmarkData,
    bookmarkFolderList,
    pickData,
    pickFolderList,
    onBookmarkChange,
    onPickChange,
    onBookmarkFolderCreateBtnSubmit,
    onPickFolderCreateBtnSubmit,
    onBookmarkFolderListRequest,
    onPickFolderListRequest,
    onMoreBtnClick
  } = props;
  const [folderListModalOpen, setFolderListModalOpen] = useState(false);
  const [alreadyFolderListRequest, setAlreadyFolderListRequest] =
    useState(false);

  const history = useHistory();

  const getPickIconElement = () => {
    if (!pickData) {
      return null;
    } else if (pickData.isPicked) {
      return (
        <IconButtonView
          type={"picked"}
          onClick={handlePickButtonClick}
          buttonSize={"small"}
          fontSize={"medium"}
          color={"forPick"}
        />
      );
    } else {
      return (
        <IconButtonView
          type={"unpicked"}
          onClick={handlePickButtonClick}
          buttonSize={"small"}
          fontSize={"medium"}
          color={"default"}
        />
      );
    }
  };

  const getIconElement = () => {
    switch (typeOfUser) {
      case "owner":
        return (
          <div>
            {getPickIconElement()}
            <IconButtonView
              type={MORE_BTN_TYPE}
              onClick={onMoreBtnClick}
              buttonSize={"small"}
              fontSize={"small"}
            />
          </div>
        );

      case "others":
        if (!bookmarkData) {
          return null;
        } else if (bookmarkData.isBookmarked) {
          return (
            <IconButtonView
              type={"bookmarked"}
              onClick={handleBookmarkButtonClick}
              buttonSize={"small"}
              fontSize={"medium"}
              color={"primary"}
            />
          );
        } else {
          return (
            <IconButtonView
              type={"unbookmarked"}
              onClick={handleBookmarkButtonClick}
              buttonSize={"small"}
              fontSize={"medium"}
              color={"default"}
            />
          );
        }

      case "guest":
      case "pending":
        return null;

      default:
        console.error(`wrong typeOfUser : "${typeOfUser}"`);
        return null;
    }
  };

  const getNextUrl = () => {
    const nextUrl = getFullUrl({
      url: getEssentialUrl({ type: "portfolio", allowRefParam: true })(
        info.ownerName
      )
    });

    return nextUrl;
  };

  const getCalendarDate = () => {
    return calendarData ? calendarData.date : null;
  };

  const isLoadingNow = () => {
    const { ownerName } = info;
    return !ownerName;
  };

  const handleOwnerNameBtnClick = () => {
    if (!info) return null;
    const nextUrl = getNextUrl();
    history.push(nextUrl);
  };

  const handleFolderListModalClose = () => {
    setFolderListModalOpen(false);
  };

  const handleBookmarkModalOpen = () => {
    if (!alreadyFolderListRequest) {
      onBookmarkFolderListRequest();
      setAlreadyFolderListRequest(true);
    }
    setFolderListModalOpen(true);
  };

  const handlePickModalOpen = () => {
    if (!alreadyFolderListRequest) {
      onPickFolderListRequest();
      setAlreadyFolderListRequest(true);
    }
    setFolderListModalOpen(true);
  };

  const handleBookmarkButtonClick = () => {
    if (!bookmarkData) return null;

    if (!bookmarkData.isBookmarked) {
      onBookmarkChange({ hash: "all" });
    }
    handleBookmarkModalOpen();
  };

  const handlePickButtonClick = () => {
    if (!pickData) return null;

    if (!pickData.isPicked) {
      onPickChange({ hash: "all" });
    }
    handlePickModalOpen();
  };

  const handleBookmarkListButtonClick = ({ hash, name }) => {
    onBookmarkChange({ hash });
    if (hash === "all") {
      handleFolderListModalClose();
    }
  };

  const handlePickListButtonClick = ({ hash, name }) => {
    onPickChange({ hash });
    if (hash === "all") {
      handleFolderListModalClose();
    }
  };

  const {
    folderListModalType,
    folderListModalDataObj,
    folderList,
    handleListBtnClick,
    onFolderCreateBtnSubmit
  } = (() => {
    let folderListModalType,
      folderListModalDataObj,
      folderList,
      handleListBtnClick,
      onFolderCreateBtnSubmit;
    switch (typeOfUser) {
      case "others":
        folderListModalType = "bookmark";
        folderListModalDataObj = bookmarkData;
        folderList = bookmarkFolderList;
        handleListBtnClick = handleBookmarkListButtonClick;
        onFolderCreateBtnSubmit = onBookmarkFolderCreateBtnSubmit;
        break;
      case "owner":
        folderListModalType = "pick";
        folderListModalDataObj = pickData;
        folderList = pickFolderList;
        handleListBtnClick = handlePickListButtonClick;
        onFolderCreateBtnSubmit = onPickFolderCreateBtnSubmit;
        break;

      default:
        break;
    }
    return {
      folderListModalType,
      folderListModalDataObj,
      folderList,
      handleListBtnClick,
      onFolderCreateBtnSubmit
    };
  })();

  const render = () => {
    const { ownerName } = info;

    return (
      <div
        style={
          getDeviceType() === "desktop" ? css.rootWrapperForDesktop : undefined
        }
      >
        <div style={css.root}>
          <div style={css.nametagArea}>
            <div style={isLoadingNow() ? css.textWrapperNowLoading : undefined}>
              <div style={css.textWrapper}>
                <NameTagTooltipView>
                  <div>
                    <TextButtonView
                      fontSize={USERNAME_FONT_SIZE}
                      fontWeight={fontCss.bold}
                      onClick={handleOwnerNameBtnClick}
                      disabled={!ownerName}
                      wordBreakCenter
                    >
                      {ownerName || "username"}
                    </TextButtonView>
                  </div>
                </NameTagTooltipView>
                {/* <span style={css.secondLineTextWrapper}>
                  <CustomTypography color="secondary" variant="subtitle1">
                    <GenderLocationInfoText
                      gender={info.gender}
                      location={info.userLocation}
                    />
                  </CustomTypography>
                </span> */}
                <span
                  style={
                    typeOfUser !== "owner" ? css.thirtLineTextRemove : undefined
                  }
                >
                  <CustomTypography color="secondary" variant="subtitle1">
                    <span
                      style={
                        !getCalendarDate()
                          ? css.thirtLineTextInvisible
                          : undefined
                      }
                    >
                      <DateText
                        inputType={"fullDateText"}
                        outputType={"outputYMD"}
                        fullDateText={getCalendarDate()}
                        displayWeekday={true}
                      />
                    </span>
                  </CustomTypography>
                </span>
              </div>
            </div>
          </div>
          {ownerName && <div style={css.btnArea}>{getIconElement()}</div>}
          {(typeOfUser === "owner" || typeOfUser === "others") && (
            <FolderListModalView
              type={folderListModalType}
              open={folderListModalOpen}
              dataObj={folderListModalDataObj}
              folderList={folderList}
              onClose={handleFolderListModalClose}
              onFolderCreateBtnSubmit={onFolderCreateBtnSubmit}
              onListButtonClick={handleListBtnClick}
            />
          )}
        </div>
      </div>
    );
  };

  return render();
}

export default NameTagView;
